import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { ApiError } from './types'
import { handleApiError } from '~/errors/handleApiError'

export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  // Never auto-retry a DB statement timeout (57014): a retry just re-incurs the
  // full timeout. The manual "Retry" affordance is the recovery path.
  if (error instanceof ApiError && error.envelope.code === '57014') return false
  // 4xx client errors are deterministic — don't retry.
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
  return failureCount < 2
}

export function AppQueryProvider({ children }: { children: ReactNode }) {
  const [client] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            try {
              handleApiError(error)
            } catch {
              // Boundary-tier errors are intentionally not propagated through
              // the QueryCache event loop — the component render path handles
              // them via query.error if needed.
            }
          },
        }),
        mutationCache: new MutationCache({
          onError: (error) => {
            try {
              handleApiError(error)
            } catch {
              // Boundary-tier errors handled via mutation.error in component code if needed.
            }
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000,
            retry: shouldRetryQuery,
            refetchOnWindowFocus: false,
          },
        },
      }),
  )
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>
}
